import { useOutletContext } from 'react-router-dom';

import AnalyticsTable from '../../../components/AiSpend/AnalyticsTable';
import BarChart from '../../../components/Charts/BarChart';
import ChartCard from '../../../components/Charts/ChartCard';
import LineChart from '../../../components/Charts/LineChart';
import ErrorState from '../../../components/DataDisplay/ErrorState';
import SpendHeatmap from '../../../components/AiSpend/SpendHeatmap';
import { getAiSpendHeatmap, getAiSpendTimeseries } from '../../../services/aiSpendService';
import { formatCount, formatMoney } from '../../../utils/helpers/aiSpendFormat';
import useApiResource from '../../../utils/hooks/useApiResource';

const BUCKETS = [
  ['Day', 'day'],
  ['Week', 'week'],
  ['Month', 'month'],
];

const GROUPINGS = [
  ['Total', 'none'],
  ['Provider', 'provider'],
  ['Feature', 'feature'],
  ['Model', 'model'],
];

const segmented = (isActive) =>
  `rounded-lg border px-3 py-1.5 text-sm font-medium ${
    isActive
      ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
      : 'border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700'
  }`;

export default function AiSpendTimeline() {
  const { filters, setFilters, queryParams, queryKey } = useOutletContext();
  const bucket = filters.bucket;
  const groupBy = filters.group_by;

  const { data, error, loading, refetch } = useApiResource(
    ({ signal }) =>
      getAiSpendTimeseries({
        params: { ...queryParams, bucket, group_by: groupBy },
        signal,
      }),
    { deps: [`${bucket}:${groupBy}:${queryKey}`] },
  );

  const heatmap = useApiResource(
    ({ signal }) => getAiSpendHeatmap({ params: queryParams, signal }),
    { deps: [queryKey] },
  );

  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const buckets = data?.buckets || [];
  const series = data?.series || [];
  const rows = buckets.map((entry, index) => ({
    key: entry.key,
    label: entry.label,
    cost_usd: series.reduce((total, one) => total + (one.cost_usd?.[index] || 0), 0),
    calls: series.reduce((total, one) => total + (one.calls?.[index] || 0), 0),
    failed_calls: series.reduce(
      (total, one) => total + (one.failed_calls?.[index] || 0),
      0,
    ),
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase text-slate-500">Bucket</span>
          {BUCKETS.map(([label, value]) => (
            <button
              key={value}
              type="button"
              className={segmented(bucket === value)}
              onClick={() => setFilters({ bucket: value })}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase text-slate-500">Split by</span>
          {GROUPINGS.map(([label, value]) => (
            <button
              key={value}
              type="button"
              className={segmented(groupBy === value)}
              onClick={() => setFilters({ group_by: value })}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <section>
        <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
          Spend over time
        </h2>
        <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
          Bucket labels come from the server, already resolved in{' '}
          {data?.timezone || 'the reporting timezone'} — the browser never derives a
          day boundary from a timestamp.
        </p>
        <ChartCard
          title={groupBy === 'none' ? 'Total spend' : `Spend by ${groupBy}`}
          loading={loading}
          isEmpty={!loading && rows.every((row) => !row.cost_usd)}
          footer={
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Only cost and call counts are plotted: quantities are in different
              units per provider and adding them would produce a number with no
              meaning.
            </p>
          }
        >
          {groupBy === 'none' ? (
            <LineChart
              ariaLabel="Spend over time"
              labels={buckets.map((entry) => entry.label)}
              datasets={[
                {
                  key: 'cost',
                  label: 'Cost',
                  data: rows.map((row) => row.cost_usd || 0),
                },
              ]}
            />
          ) : (
            <BarChart
              stacked
              ariaLabel="Spend over time by group"
              labels={buckets.map((entry) => entry.label)}
              datasets={series.map((one) => ({
                key: one.key,
                label: one.label,
                data: (one.cost_usd || []).map((value) => value || 0),
              }))}
            />
          )}
        </ChartCard>

        <div className="mt-4" />

        <AnalyticsTable
          loading={loading}
          rows={rows}
          rowKey={(row) => row.key}
          columns={[
            { key: 'label', label: 'Bucket', render: (row) => row.label },
            {
              key: 'cost_usd',
              label: 'Cost',
              render: (row) => formatMoney(row.cost_usd),
              className: 'font-semibold',
            },
            { key: 'calls', label: 'Calls', render: (row) => formatCount(row.calls) },
            {
              key: 'failed_calls',
              label: 'Failed',
              render: (row) => formatCount(row.failed_calls),
            },
          ]}
        />
      </section>

      <section>
        <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
          When the spend happens
        </h2>
        <SpendHeatmap
          cells={heatmap.data?.cells || []}
          maxCost={heatmap.data?.max_cost_usd || 0}
          timezone={heatmap.data?.timezone}
          loading={heatmap.loading}
        />
      </section>
    </div>
  );
}
